import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { toAnchored, toWorld } from "@board/shared";
import { colorOf, useBoard } from "./store";
import type { Transform } from "./useCanvasTransform";

/**
 * A client column as the ink, text and attachment layers see it: where it is
 * drawn right now, and how big.
 *
 * It used to carry a storeDx/storeDy pair as well — the difference between
 * where a column was stored and where focus mode drew it — because content
 * stored absolute coordinates and had to be shifted back and forth. Content is
 * stored relative to its column now (migration 017), so there is nothing to
 * shift: x and y *are* the anchor, in whichever view is on screen.
 */
export type InkRegion = {
  clientId: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

function pathOf(points: [number, number][]): string {
  if (!points.length) return "";
  if (points.length === 1) return `M${points[0]![0]} ${points[0]![1]}l0.1 0`;
  let path = `M${points[0]![0]} ${points[0]![1]}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const point = points[index]!;
    const next = points[index + 1]!;
    path += ` Q${point[0]} ${point[1]} ${(point[0] + next[0]) / 2} ${(point[1] + next[1]) / 2}`;
  }
  const last = points[points.length - 1]!;
  return `${path} L${last[0]} ${last[1]}`;
}

function distanceToSegment(point: [number, number], start: [number, number], end: [number, number]) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = dx * dx + dy * dy;
  const t = length === 0 ? 0 : Math.max(0, Math.min(1,
    ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / length,
  ));
  return Math.hypot(point[0] - (start[0] + t * dx), point[1] - (start[1] + t * dy));
}

export function InkLayer({ transform, screenToWorld, regions, focusedClientId }: {
  transform: Transform;
  screenToWorld: (x: number, y: number) => { x: number; y: number };
  regions: InkRegion[];
  focusedClientId: string | null;
}) {
  const scale = transform.scale;
  const tool = useBoard((state) => state.tool);
  const markerColor = useBoard((state) => state.markerColor);
  const annotations = useBoard((state) => state.annotations);
  const drawAnnotation = useBoard((state) => state.drawAnnotation);
  const eraseAnnotation = useBoard((state) => state.eraseAnnotation);
  const [draft, setDraft] = useState<[number, number][]>([]);
  const [problem, setProblem] = useState("");
  const active = useRef<{ pointer: number; region: InkRegion | null } | null>(null);
  const erased = useRef(new Set<string>());

  useEffect(() => {
    if (tool !== "pen" && tool !== "eraser") setProblem("");
  }, [tool]);

  const displayed = useMemo(() => annotations
    .filter((annotation) => !focusedClientId || annotation.client_id === focusedClientId)
    .map((annotation) => {
      const anchor = regions.find((candidate) => candidate.clientId === annotation.client_id) ?? null;
      return { ...annotation, displayPoints: annotation.path_data.points.map(([x, y]) => {
        const point = toWorld(anchor, { x, y });
        return [point.x, point.y] as [number, number];
      }) };
    }), [annotations, focusedClientId, regions]);

  const regionAt = (point: [number, number]) => regions.find((region) =>
    point[0] >= region.x && point[0] <= region.x + region.width
    && point[1] >= region.y && point[1] <= region.y + region.height) ?? null;

  function eraseAt(point: [number, number]) {
    const radius = 14 / scale;
    for (const annotation of displayed) {
      if (annotation.id.startsWith("temp-") || erased.current.has(annotation.id)) continue;
      const hit = annotation.displayPoints.some((candidate, index) => index === 0
        ? Math.hypot(candidate[0] - point[0], candidate[1] - point[1]) <= radius
        : distanceToSegment(point, annotation.displayPoints[index - 1]!, candidate)
          <= radius + annotation.width / 2);
      if (hit) {
        erased.current.add(annotation.id);
        void eraseAnnotation(annotation.id).catch(() => {
          erased.current.delete(annotation.id);
          setProblem("Couldn’t erase that line. Try again.");
        });
      }
    }
  }

  function down(event: ReactPointerEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const world = screenToWorld(event.clientX, event.clientY);
    const point: [number, number] = [world.x, world.y];
    active.current = { pointer: event.pointerId, region: regionAt(point) };
    erased.current.clear();
    setProblem("");
    event.currentTarget.setPointerCapture(event.pointerId);
    if (tool === "pen") setDraft([point]);
    else eraseAt(point);
  }

  function move(event: ReactPointerEvent) {
    if (active.current?.pointer !== event.pointerId) return;
    const world = screenToWorld(event.clientX, event.clientY);
    const point: [number, number] = [world.x, world.y];
    if (tool === "eraser") return eraseAt(point);
    setDraft((points) => {
      const last = points[points.length - 1];
      if (last && Math.hypot(point[0] - last[0], point[1] - last[1]) < 1.5 / scale) return points;
      return points.length >= 5000 ? points : [...points, point];
    });
  }

  function up(event: ReactPointerEvent) {
    const drawing = active.current;
    if (!drawing || drawing.pointer !== event.pointerId) return;
    active.current = null;
    if (tool !== "pen" || !draft.length) return;
    const region = drawing.region;
    // A stroke belongs to the column it was started on, and is stored against
    // that column's origin so it travels with it.
    const points = draft.map(([x, y]) => {
      const point = toAnchored(region, { x, y });
      return [point.x, point.y] as [number, number];
    });
    setDraft([]);
    void drawAnnotation({ client_id: region?.clientId ?? null, type: "freehand",
      path_data: { points }, color: colorOf(markerColor), width: 5 })
      .catch(() => setProblem("Couldn’t save that line. Try again."));
  }

  return (
    <>
      <svg className="inklayer" aria-hidden="true" style={{
        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
      }}>
        {displayed.map((annotation) => <path key={annotation.id}
          d={pathOf(annotation.displayPoints)} fill="none" stroke={annotation.color}
          strokeWidth={annotation.width} strokeLinecap="round" strokeLinejoin="round" />)}
        {draft.length ? <path d={pathOf(draft)} fill="none" stroke={colorOf(markerColor)}
          strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" /> : null}
      </svg>
      {(tool === "pen" || tool === "eraser") ? <div className={`inkcapture inkcapture--${tool}`}
        onPointerDown={down} onPointerMove={move} onPointerUp={up}
        onPointerCancel={(event) => {
          if (active.current?.pointer !== event.pointerId) return;
          active.current = null;
          setDraft([]);
        }} /> : null}
      {problem ? <p className="toolproblem" role="alert">{problem}</p> : null}
    </>
  );
}
